import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { SchafService } from '../schafe/schaf.service';
import { ToastService } from '../../components/ui/toast/toast.services';
import { GlobalLoadingStore } from '../../components/ui/loadingSpinner/global-loading.store';

const SCAN_KEY_THRESHOLD_MS = 40;
const FLUSH_DELAY_MS = SCAN_KEY_THRESHOLD_MS + 15; // Puffer über der Scan-Schwelle
const MIN_CODE_LENGTH = 10;
const BUFFER_RESET_MS = 200;

type FocusTarget = HTMLInputElement | HTMLTextAreaElement;

interface PendingChar {
  key: string;
  target: FocusTarget | null;
  selectionStart: number | null;
  selectionEnd: number | null;
}

@Injectable({ providedIn: 'root' })
export class RfidScannerService {
  readonly router = inject(Router);
  readonly schafService = inject(SchafService);
  readonly toast = inject(ToastService);
  readonly loadingStore = inject(GlobalLoadingStore);

  readonly enabled = signal(true);

  // Scan-Erkennung (unverändert)
  private buffer = '';
  private lastKeyTime = 0;
  private isScanning = false;
  private resetTimer?: ReturnType<typeof setTimeout>;
  private lastProcessedCode = '';
  private lastProcessedAt = 0;

  // NEU: Zurückhalten der Zeichen, bis entschieden ist, ob Scan oder Tippen
  private pendingChars: PendingChar[] = [];
  private flushTimer?: ReturnType<typeof setTimeout>;

  private initialized = false;

  init(): void {
    if (this.initialized) return;
    this.initialized = true;
    document.addEventListener('keydown', (e) => this.handleKeydown(e));
    document.body.tabIndex = -1;

    // Der Scanner sendet reine Tastatur-Events (kein Klick). Direkt nach dem App-Start
    // liegt der Fokus oft noch außerhalb der Seite (z. B. Adressleiste, Keycloak-Redirect),
    // wodurch keydown-Events das document nie erreichen. Ein einmaliger focus()-Aufruf im
    // Konstruktor kommt oft zu früh (vor Guard-Auflösung / bevor der Browser der Adressleiste
    // selbst den Fokus entzieht) und wird danach vom Browser wieder überschrieben. Fokus daher
    // mehrfach anfordern: sofort, nach vollständigem Laden und beim (Zurück-)Aktivieren des Tabs.
    this.claimFocus();
    window.addEventListener('load', () => this.claimFocus());
    window.addEventListener('focus', () => this.claimFocus());
  }

  /** Holt den Fokus in die Seite zurück, ohne einen aktiven Eingabefokus des Nutzers zu stehlen */
  claimFocus(): void {
    const active = document.activeElement;
    if (active && active !== document.body && active !== document.documentElement) return;
    document.body.focus({ preventScroll: true });
  }

  private handleKeydown(event: KeyboardEvent): void {
    if (!this.enabled()) return;

    const now = Date.now();
    const delta = now - this.lastKeyTime;
    this.lastKeyTime = now;

    if (event.key === 'Enter') {
      if (this.isScanning && this.buffer.length >= MIN_CODE_LENGTH) {
        // Bestätigter Scan → alles verwerfen, niemals ins Feld einfügen
        event.preventDefault();
        event.stopPropagation();
        clearTimeout(this.flushTimer);
        this.pendingChars = [];
        const code = this.buffer;
        this.resetBuffer();
        this.onScan(code);
      } else {
        // Kein Scan → noch ausstehende Zeichen sofort einfügen,
        // dann den Enter NICHT blockieren (normales Form-Verhalten erlauben)
        clearTimeout(this.flushTimer);
        this.flushPendingChars();
        this.resetBuffer();
      }
      return;
    }

    if (event.key.length !== 1) return;

    // Scan-Erkennung wie bisher (sticky)
    if (this.buffer.length === 0) {
      this.isScanning = false;
    } else if (delta < SCAN_KEY_THRESHOLD_MS) {
      this.isScanning = true;
    }
    this.buffer += event.key;

    // IMMER preventDefault — das Zeichen wird zurückgehalten, nie sofort eingefügt
    event.preventDefault();
    event.stopPropagation();

    const target = event.target instanceof HTMLElement ? event.target : null;
    this.pendingChars.push({
      key: event.key,
      target: this.isTextInput(target) ? target : null,
      selectionStart: this.safeSelection(target, 'selectionStart'),
      selectionEnd: this.safeSelection(target, 'selectionEnd'),
    });

    clearTimeout(this.flushTimer);
    this.flushTimer = setTimeout(() => this.decideAndFlush(), FLUSH_DELAY_MS);

    clearTimeout(this.resetTimer);
    this.resetTimer = setTimeout(() => this.resetBuffer(), BUFFER_RESET_MS);
  }

  /** Wird aufgerufen, wenn FLUSH_DELAY_MS ohne weiteres Zeichen verstrichen ist */
  private decideAndFlush(): void {
    if (this.isScanning) {
      // War (bisher) ein Scan-Burst, aber kein Enter kam → wahrscheinlich Pause mitten im Scan.
      // Wir warten weiter (BUFFER_RESET_MS regelt das endgültige Verwerfen).
      return;
    }
    this.flushPendingChars();
  }

  /** Fügt zurückgehaltene Zeichen tatsächlich ins ursprüngliche Feld ein */
  private flushPendingChars(): void {
    if (this.pendingChars.length === 0) return;

    for (const pending of this.pendingChars) {
      if (!pending.target) continue;
      this.insertCharIntoField(pending.target, pending.key, pending.selectionStart, pending.selectionEnd);
    }
    this.pendingChars = [];
  }

  private insertCharIntoField(
    target: FocusTarget,
    char: string,
    start: number | null,
    end: number | null,
  ): void {
    const value = target.value ?? '';
    const s = start ?? value.length;
    const e = end ?? value.length;

    target.value = value.slice(0, s) + char + value.slice(e);

    const newPos = s + char.length;
    try {
      target.setSelectionRange(newPos, newPos);
    } catch {
      // type="number" etc. unterstützt setSelectionRange nicht — ignorieren
    }

    // WICHTIG: Angular Reactive Forms hören auf das native 'input'-Event
    target.dispatchEvent(new Event('input', { bubbles: true }));
  }

  private isTextInput(el: HTMLElement | null): el is FocusTarget {
    return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;
  }

  /** input[type=number] wirft bei selectionStart/-End einen Fehler — sicher abfragen */
  private safeSelection(
    el: HTMLElement | null,
    prop: 'selectionStart' | 'selectionEnd',
  ): number | null {
    if (!this.isTextInput(el)) return null;
    try {
      return el[prop];
    } catch {
      return null;
    }
  }

  private resetBuffer(): void {
    this.buffer = '';
    this.isScanning = false;
  }

  private onScan(code: string): void {
    if (!/^\d{10,15}$/.test(code)) {
      console.warn('Ungültiges Scan-Format ignoriert:', code);
      return;
    }

    const now = Date.now();
    if (code === this.lastProcessedCode && now - this.lastProcessedAt < 1500) return;
    this.lastProcessedCode = code;
    this.lastProcessedAt = now;

    this.loadingStore.withGlobalLoading(this.schafService.getSchafByTransponder(code)).subscribe({
      next: (response: any) => {
        const schaf = response.data ?? response;
        if (!schaf?.id) {
          this.toast.error(`Kein Tier mit dem Transponder ${code} gefunden`);
          return;
        }
        this.router.navigate(['/bestand', schaf.id, 'edit']);
      },
      error: () => {
        this.toast.error('Kein Tier mit dem Transponder gefunden');
      },
    });
  }
}
